import React, { useEffect, useRef, useState } from 'react';
import { Box, Card, IconButton, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { CouponMasterItemTableRow, CouponPackMasterFormInput } from '../../../../types/couponPackMaster';
import { theme } from '../../../../theme';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { Image } from '@material-ui/icons';
import { Pagination } from '../../../../components/molecules/Pagination';
import { formatJstDate } from '../../../../util';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';

type PackSmartCheckTemplateProps = {
  open?: boolean;
  input: CouponPackMasterFormInput;
  logoImageUrl?: string;
  couponImageUrl?: string;
  setStatus: React.Dispatch<React.SetStateAction<string>>;
  setCurrentCouponSelected: React.Dispatch<React.SetStateAction<CouponMasterItemTableRow | undefined>>;
  displayCouponPackName?: string;
};

const useStyles = makeStyles(() => ({
  header: {
    display: 'flex',
    justifyContent: 'center',
    padding: '20px',
    maxHeight: '60px',
    top: 0,
    backgroundColor: '#FFFFFF',
    width: '100%',
    zIndex: 1000,
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
  },
  root: {
    backgroundColor: theme.palette.common.white,
  },
  wrapperContent: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  content: {
    padding: '0 20px 40px 20px',
  },
  heroImage: {
    width: '100%',
    height: '150px',
    display: 'block',
  },
  title: {
    fontSize: 18,
    fontWeight: 600,
    color: '#303030',
    lineHeight: 1.6,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '190px',
    '& > span': {
      fontSize: 14,
    },
    marginBottom: '9px',
  },
  titleSpan: {
    fontSize: 14,
    fontWeight: 300,
  },
  banner: {
    width: 'calc(100% + 32px)',
    marginLeft: '-16px',
    marginRight: '-16px',
    marginTop: '-16px',
    marginBottom: '16px',
    '& img': {
      width: '100%',
      display: 'block',
    },
  },
  pageInfo: {
    marginTop: '24px',
  },
  card: {
    display: 'flex',
    alignItems: 'center',
    padding: '18px 16px 18px 20px',
    marginTop: theme.spacing(2),
    borderRadius: 16,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    boxSizing: 'border-box',
    position: 'relative',
  },
  containerImg: {
    width: 102,
    height: 102,
    minWidth: 102,
    overflow: 'hidden',
  },
  couponImage: {
    width: '100%',
    height: '100%',
    borderRadius: 8,
    objectFit: 'contain',
    flexShrink: 0,
  },
  contentWrapper: {
    flex: 1,
    marginLeft: theme.spacing(1.5),
    display: 'flex',
    flexDirection: 'column',
  },
  titleCard: {
    fontSize: 14,
    fontWeight: 600,
    color: '#303030',
    lineHeight: 1.4,
    marginBottom: theme.spacing(0.5),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
  },
  expiryDate: {
    fontSize: 14,
    fontWeight: 600,
    color: '#303030',
    lineHeight: '160%',
    marginTop: theme.spacing(0.5),
    '& span': {
      color: '#FF3B30',
    },
  },
  remainingWrapper: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 4,
    marginTop: theme.spacing(0.5),
  },
  remainingLabel: {
    fontSize: 12,
    fontWeight: 300,
    color: '#303030',
    lineHeight: 1,
  },
  remainingCount: {
    fontSize: 22,
    fontWeight: 600,
    color: '#FF3B30',
    lineHeight: 1,
  },
  remainingUnit: {
    fontSize: 12,
    fontWeight: 300,
    color: '#303030',
    lineHeight: 1,
  },
  chevronIcon: {
    fontSize: 24,
    color: '#BDBDBD',
    flexShrink: 0,
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    borderRadius: 16,
    background: '#C6C6C6',
    pointerEvents: 'none',
    opacity: 0.7,
  },
  arrowUp: {
    position: 'fixed',
    bottom: '50px',
    right: 'calc(50% - 417px / 2 + 30px)',
    width: '60px',
    height: '60px',
    cursor: 'pointer',
    backgroundColor: '#FF3B30',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '999px',
    zIndex: 1001,
    '@media (max-width: 417px)': {
      right: '12px',
    },
  },
  couponName: {
    fontSize: 26,
    fontWeight: 600,
    color: '#303030',
    lineHeight: 1.6,
    marginBottom: '9px',
    textAlign: 'center',
    padding: '0 20px',
    wordBreak: 'break-word',
  },
  couponAmount: {
    fontSize: 16,
    color: '#FF3B30',
    fontWeight: 600,
    display: 'inline-block',
    lineHeight: 0,
  },
  couponAmountUnit: {
    fontSize: 10,
    fontWeight: 300,
    display: 'inline-block',
    lineHeight: 0,
  },
}));

export const PackSmartCheckTemplate: React.FC<PackSmartCheckTemplateProps> = (props) => {
  const { input, open, setStatus, setCurrentCouponSelected } = props;
  const classes = useStyles();
  const [currentPage, setCurrentPage] = useState(1);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const itemsPerPage = 10;
  const dataDisplayValid = input.products.filter((product) => {
    return (
      new Date(product.displayStartDate as string) <= new Date() &&
      new Date(product.displayEndDate as string) >= new Date()
    );
  });

  const sortedDataNoExpiry = dataDisplayValid
    .filter((product) => new Date(product.validityEndDateTime as string) >= new Date())
    .sort((a, b) => {
      const dateA = new Date(a.validityEndDateTime as string).getTime();
      const dateB = new Date(b.validityEndDateTime as string).getTime();
      return dateA - dateB;
    });

  const sortedDataWithExpiry = dataDisplayValid
    .filter((product) => new Date(product.validityEndDateTime as string) < new Date())
    .sort((a, b) => {
      const dateA = new Date(a.validityEndDateTime as string).getTime();
      const dateB = new Date(b.validityEndDateTime as string).getTime();
      return dateA - dateB;
    });
  const sortedProducts = [...sortedDataNoExpiry, ...sortedDataWithExpiry];

  const totalPages = Math.ceil(sortedProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentProducts = sortedProducts.slice(startIndex, endIndex);
  const handleScrollToTop = (): void => {
    let el: Element | null = scrollContainerRef.current;
    while (el) {
      const style = window.getComputedStyle(el);
      if (style.overflowY === 'auto' || style.overflowY === 'scroll') break;
      el = el.parentElement;
    }
    el?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    if (!open) {
      setShowScrollTop(false);
      return;
    }

    const timer = setTimeout(() => {
      let el: Element | null = scrollContainerRef.current;
      while (el) {
        const style = window.getComputedStyle(el);
        if (style.overflowY === 'auto' || style.overflowY === 'scroll') break;
        el = el.parentElement;
      }
      const scrollContainer = el;
      if (!scrollContainer) return;

      const handleScroll = (): void => {
        if (scrollContainer.scrollTop > 20) {
          setShowScrollTop(true);
        } else {
          setShowScrollTop(false);
        }
      };

      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
        scrollContainer.removeEventListener('scroll', handleScroll);
      };
    }, 0);

    return () => {
      clearTimeout(timer);
    };
  }, [open]);

  return (
    <div ref={scrollContainerRef} className={classes.root}>
      <Box className={classes.wrapperContent}>
        {!!props.couponImageUrl && (
          <Box mb={2}>
            <img src={props.couponImageUrl} alt="Coupon" className={classes.heroImage} />
          </Box>
        )}
        <Box className={classes.content}>
          <Typography className={classes.title}>
            <span className={classes.titleSpan}>全 </span>
            {sortedProducts.length}
            <span className={classes.titleSpan}> 件</span>
          </Typography>
          {currentProducts.map((product, index) => (
            <Card
              key={index}
              className={classes.card}
              elevation={0}
              onClick={() => {
                setStatus('INPUT');
                setCurrentCouponSelected(product);
              }}
            >
              {new Date(product.validityEndDateTime as string) < new Date() && <Box className={classes.overlay} />}
              <Box className={classes.containerImg}>
                {product.imageUrl ? (
                  <img src={product.imageUrl} alt={product.couponMasterName} className={classes.couponImage} />
                ) : (
                  <IconButton className={classes.couponImage}>
                    <Image />
                  </IconButton>
                )}
              </Box>
              <Box className={classes.contentWrapper}>
                <Typography
                  className={classes.titleCard}
                  style={{ marginBottom: !product.displayValidityDate ? '0px' : undefined }}
                >
                  {product.couponNameHtml ? product.couponNameHtml : product.couponMasterName}
                </Typography>
                {product.displayValidityDate && (
                  <Typography className={classes.expiryDate}>
                    有効期限：
                    <span>{formatJstDate(product.validityEndDateTime)}</span>
                  </Typography>
                )}
                <Box
                  display="flex"
                  alignItems="end"
                  justifyContent="space-between"
                  flexWrap="wrap"
                  mt={0.5}
                  style={{ gap: 6 }}
                >
                  <Box className={classes.remainingWrapper}>
                    <Typography className={classes.remainingLabel}>残り</Typography>
                    <Typography className={classes.remainingCount}>{product.quantity}</Typography>
                    <Typography className={classes.remainingUnit}>枚</Typography>
                  </Box>
                  {Number(product.couponAmount) > 0 && Number(product.quantity) >= 0 && (
                    <Box lineHeight={'18px'} marginLeft={'auto'} textAlign={'right'}>
                      <Typography className={classes.couponAmount}>
                        {(Number(product.couponAmount) * Number(product.quantity)).toLocaleString('en-US')}
                      </Typography>
                      <Typography className={classes.couponAmountUnit}>円分</Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              <ChevronRightIcon className={classes.chevronIcon} style={{ color: '#FF3B30' }} />
            </Card>
          ))}
          {totalPages > 1 && (
            <Box mt={3}>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={(page) => {
                  //scroll to top when page changes
                  handleScrollToTop();
                  setCurrentPage(page);
                }}
                totalItems={sortedProducts.length}
                itemsPerPage={itemsPerPage}
              />
            </Box>
          )}
        </Box>
      </Box>
      {showScrollTop && (
        <Box className={classes.arrowUp} onClick={handleScrollToTop}>
          <KeyboardArrowUpIcon style={{ fontSize: 30, color: '#FFFFFF' }} />
        </Box>
      )}
    </div>
  );
};
